<template>
  <div class="member-assets-container">
    <CCard>
      <CCardHeader>
        <CRow>
          <CCol md="12" sm="12">
            <h1>{{ $t('USER_ASSET_LIST_PAGE_TITLE_PAYOUT_REQUEST') }}</h1>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody>
        <SearchUserAssetHeader :onSearch="onSearch" :platformList="platformList" @downloadCSV="downloadCSV" />

        <UserAssetListTable :limit="limit" :activePage="activePage" :items="items" :total="total" />
        <StakingPagination
          :limit="limit"
          :total="total"
          :activePage="activePage"
          @changePage="onChangePage"
          @changePageSize="onChangePageSize"
        />
      </CCardBody>
    </CCard>
  </div>
</template>
<script>
import _ from 'lodash';
import moment from 'moment';
import { mapState, mapActions, mapGetters } from 'vuex';
import UserAssetListTable from '@/components/MemberAsset/UserAssetListTable';
import SearchUserAssetHeader from '@/components/MemberAsset/SearchUserAssetHeader';
import StakingPagination from '@/components/StakingPagination';
import { endpoints, env, Constants } from '@/constants';
import responseHelper from '@/utils/response-helper';
import stringHelper from '@/utils/string-helper';
import sessionStorageHelper from '@/utils/sessionStorage-helper';
import dateTimeHelper from '@/utils/datetime-helper';
import { saveAs } from 'file-saver';

export default {
  name: 'UserAssetList',
  components: {
    UserAssetListTable,
    SearchUserAssetHeader,
    StakingPagination,
  },
  data() {
    return {
      items: [],
      platformList: [],
      filterData: {},
      total: 0,
      limit: env.app.pageSize,
      activePage: 1,
      dateFormat: Constants.dateFormat,
    };
  },
  computed: {
    offset() {
      return (this.activePage - 1) * this.limit;
    },
    pages() {
      return Math.ceil(this.total / this.limit);
    },
  },
  async mounted() {
    await Promise.all([this.getStakingPlatforms()]);
    this.isLoading = false;
  },
  methods: {
    async getPlatforms() {
      try {
        const params = {
          offset: 0,
          limit: 100000000,
        };
        const result = await this.$http.get(endpoints.getPlatforms, { params });
        this.platformList = _.sortBy(result.data.items, 'order_index').map(item => {
          const currencySymbol = item.platform === item.symbol ? item.platform : item.symbol;

          return {
            ...item,
            value: currencySymbol,
            label: currencySymbol,
          };
        });
      } catch (err) {
        this.$notify({
          group: 'notifications',
          type: 'error',
          title: this.$t('AFFILIATE_POLICIES_NOTIFY_ERROR_TITLE'),
          text: responseHelper.getErrorMessage(err),
        });
      }
    },
    async getStakingPlatforms() {
      try {
        const result = await this.$http.get(endpoints.getStakingPlatforms);
        this.platformList = result.data.map(item => {
          return {
            ...item,
            value: item.currency_symbol == 'TADA' ? 'ADA' : item.currency_symbol,
            label: item.name == 'TADA' ? 'ADA' : item.name,
          };
        });
      } catch (err) {
        this.$notify({
          group: 'notifications',
          type: 'error',
          title: this.$t('SEARCH_REWARD_REQEUST_COMPONENT_NOTIFY_ERROR_TITLE'),
          text: responseHelper.getErrorMessage(err),
        });
      }
    },
    async getMemberAssets(data) {
      data = data || {};
      this.filterData = {
        ...this.filterData,
        ...data,
      };
      const filterData = this.filterData;

      try {
        let params = {
          limit: this.limit,
          offset: this.offset,
          from_date: filterData.fromDate,
          to_date: dateTimeHelper.adjustToDate(filterData.toDate),
          email: filterData.email,
          address: filterData.address,
          platform: filterData.cryptoPlatform,
        };
        if (filterData.hasFilterPlatform) {
          params = {
            ...params,
            balanceFrom: filterData.balanceFrom,
            balanceTo: filterData.balanceTo,
            amountFrom: filterData.amountFrom,
            amountTo: filterData.amountTo,
          };
        }
        const result = await this.$http.get(endpoints.getMemberAssets, { params });
        const items = result.data.items || [];
        items.forEach(item => {
          item.rewardSymbol = item.platform;

          switch (item.platform) {
            case 'ONT':
              item.rewardSymbol = 'ONG';
              break;

            case 'ONG':
              item.rewardSymbol = 'ONT';
              break;
          }
        });
        this.items = items;

        this.total = result.data.total;
      } catch (err) {
        this.$notify({
          group: 'notifications',
          type: 'error',
          title: this.$t('USER_ASSET_LIST_PAGE_TITLE_NOTIFY_ERROR'),
          text: responseHelper.getErrorMessage(err),
        });
      }
    },
    async onChangePage(page) {
      this.activePage = page;

      this.getMemberAssets();
    },
    async onChangePageSize(value) {
      this.limit = value;
      this.activePage = 1;

      this.getMemberAssets();
    },
    async onSearch(searchData) {
      const data = searchData;
      this.activePage = 1;

      this.getMemberAssets(data);
    },
    async downloadCSV(searchData) {
      const filterData = {
        ...searchData,
      };

      let params = {
        from_date: filterData.fromDate,
        to_date: dateTimeHelper.adjustToDate(filterData.toDate),
        email: filterData.email,
        address: filterData.address,
        platform: filterData.cryptoPlatform,
        timezone_offset: new Date().getTimezoneOffset(),
      };
      if (filterData.hasFilterPlatform) {
        params = {
          ...params,
          balanceFrom: filterData.balanceFrom,
          balanceTo: filterData.balanceTo,
          amountFrom: filterData.amountFrom,
          amountTo: filterData.amountTo,
        };
      }
      try {
        let result;
        if (filterData.onlyLatest) {
          result = await this.$http.get(endpoints.downloadLatestCSVMemberAssets, { params });
        } else {
          result = await this.$http.get(endpoints.dowloadCSVMemberAssets, { params });
        }
        const data = result;
        const fileUrl = 'member-assets.csv';
        const contentType = 'application/octet-stream';
        const file = new Blob([data], { type: contentType });
        saveAs(file, fileUrl);
      } catch (err) {
        this.$notify({
          group: 'notifications',
          type: 'error',
          title: 'Error',
          text: responseHelper.getErrorMessage(err),
        });
      }
    },
  },
};
</script>
<style lang="scss">
.member-assets-container {
  padding: 0 24px;
  @media screen and (max-width: 1024px) {
    padding: 0 15px;
  }

  .card {
    > .card-header {
      border-bottom: none;
      padding-bottom: 0px;

      h1 {
        color: #657187;
        font-size: 18px;
        font-weight: 600;
        line-height: 34px;
        margin-bottom: 0px;
      }
    }
  }
}
</style>
